<script setup lang="ts">
import { computed, nextTick, ref, useAttrs, useSlots, watch } from "vue";
import { useSwipe, type UseSwipeDirection } from "@vueuse/core";
import VIcon from "@magnit/icons/src/components/VIcon/VIcon.vue";
import CloseIcon from "@magnit/icons/src/assets/icons/close-24.svg";
import { responsiveClass } from "../../utilities/helpers";
import VText from "../VText/VText.vue";
import { Viewport } from "../../constants/viewport";
import type { IModalEmits, IModalProps } from "./VModal.types";

const props = withDefaults(defineProps<IModalProps>(), {
  type: "modal",
  title: undefined,
  subtitle: undefined,
  wrapperClass: undefined,
  closable: true,
  swipeable: false,
  fullScreenBottomSheet: true,
});
const emit = defineEmits<IModalEmits>();
defineOptions({ inheritAttrs: false });

const target = ref<HTMLElement | null>(null);
const swipe = ref<HTMLElement | null>(null);

const attrs = useAttrs();
const slots = useSlots();

const hasHeader = computed(() => {
  return (
    !props.hideHeader &&
    (Boolean(props.title) ||
      Boolean(props.subtitle) ||
      props.closable ||
      Boolean(slots.headerLeft) ||
      Boolean(slots.headerRight))
  );
});

const hasTopSlot = computed(() => Boolean(slots.top));
const showTopCloseBtn = computed(() => Boolean(slots.top) && props.hideHeader);

const onHeaderLeftClick = () => {
  emit("headerLeft");
};

const onHeaderRightClick = () => {
  emit("headerRight");
};

const onHeaderRightCloseClick = () => {
  onHeaderRightClick();
  onClose();
};

const onClose = () => {
  emit("close");
};

const rootClass = computed(() => {
  return [...responsiveClass("pl-modal_type", props.type)];
});

const maxHeight = ref<number | null>(0);
const isSwiping = ref(false);

const { lengthY } = useSwipe(swipe, {
  passive: true,
  onSwipeStart() {
    if (!props.swipeable) {
      return;
    }
    isSwiping.value = true;
    maxHeight.value = target.value?.clientHeight ?? 0;
  },
  onSwipeEnd(_: TouchEvent, direction: UseSwipeDirection) {
    if (!props.swipeable) {
      return;
    }
    isSwiping.value = false;
    if (direction === "down") {
      emit("close");
    }
    if (direction === "up") {
      maxHeight.value = null;
    }
  },
});

const modalHeight = computed(() => {
  if (!props.swipeable || window.innerWidth >= Viewport.m) {
    return "initial";
  }
  if (!isSwiping.value) {
    return maxHeight.value ? `${maxHeight.value}px` : "initial";
  }
  return `${Math.round((maxHeight.value ?? 0) + lengthY.value)}px`;
});

watch(
  () => props.open,
  () => {
    if (typeof window === "undefined") {
      return;
    }
    nextTick(() => {
      const height = window.innerHeight ?? 0;
      const clientHeight = target.value?.clientHeight ?? 0;
      maxHeight.value = !props.fullScreenBottomSheet
        ? height * 0.33
        : clientHeight;
    });
  },
  { immediate: true },
);
</script>

<template>
  <client-only>
    <teleport to="body">
      <transition name="pl-modal_fade">
        <div
          v-if="props.open"
          class="pl-modal-wrapper"
          :class="[
            {
              'pl-modal-wrapper_active': props.active,
              'pl-modal-wrapper_no-padding': props.noPadding,
            },
            props.wrapperClass,
          ]"
          data-test-id="modal"
        >
          <div
            ref="target"
            class="pl-modal"
            :class="rootClass"
            :style="{ height: modalHeight }"
          >
            <div v-if="false" class="pl-modal-swipe"></div>
            <div v-if="hasHeader" ref="swipe" class="pl-modal-header__wrapper">
              <div class="pl-modal-header">
                <div class="pl-modal-header__control-wrapper">
                  <template v-if="slots.headerLeft">
                    <div
                      class="pl-modal-header__control"
                      @click="onHeaderLeftClick"
                    >
                      <slot name="headerLeft" />
                    </div>
                  </template>
                </div>

                <div class="pl-modal-header__title">
                  <VText
                    v-if="props.title"
                    tag="div"
                    class="pl-modal-header__title-text"
                    font="title-small"
                  >
                    {{ props.title }}
                  </VText>
                </div>

                <div class="pl-modal-header__control-wrapper">
                  <template v-if="slots.headerRight">
                    <div
                      class="pl-modal-header__control"
                      @click="onHeaderRightClick"
                    >
                      <slot name="headerRight" />
                    </div>
                  </template>
                  <template v-else-if="props.closable">
                    <div
                      class="pl-modal-header__control"
                      @click="onHeaderRightCloseClick"
                    >
                      <VIcon><CloseIcon /></VIcon>
                    </div>
                  </template>
                </div>
              </div>

              <div v-if="props.subtitle" class="pl-modal-header__subtitle">
                <VText color="secondary">{{ props.subtitle }}</VText>
              </div>
            </div>

            <div v-if="hasTopSlot" class="pl-modal-top">
              <slot name="top" />

              <div
                v-if="showTopCloseBtn"
                class="pl-modal-top__control"
                @click="onHeaderRightCloseClick"
              >
                <VIcon><CloseIcon /></VIcon>
              </div>
            </div>

            <div v-if="slots.footer" class="pl-modal-footer">
              <slot name="footer" />
            </div>

            <div v-bind="attrs" class="pl-modal-content">
              <div class="pl-modal-content-wrapper">
                <slot />
              </div>
            </div>
          </div>
          <div class="pl-modal-scrim" @click.self="onClose" />
        </div>
      </transition>
    </teleport>
  </client-only>
</template>

<style lang="postcss" scoped>
.pl-modal-wrapper {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: var(--pl-z-index-overlay);
  transition: opacity 0.3s ease;
  display: none;

  &_active {
    display: block;
  }

  &.pl-modal_fade-enter-from,
  &.pl-modal_fade-leave-to {
    opacity: 0;
  }
}

.pl-modal {
  background-color: var(--pl-global-background-primary);
  position: absolute;
  display: flex;
  flex-direction: column;
  transition: transform 0.3s ease;
  overflow: hidden;
  touch-action: none;

  &-swipe {
    position: absolute;
    display: none;
    height: var(--pl-unit-x1);
    border-radius: 1000px;
    width: 44px;
    top: 7px;
    margin: auto;
    background-color: var(--pl-surface-subdued-default);
    z-index: var(--pl-z-index-toast);
    left: 50%;
    transform: translate(-50%, -50%);
  }

  &_type-bottom-sheet {
    .pl-modal-swipe {
      display: block;
    }

    border-radius: var(--pl-bottomsheet-radius-l) var(--pl-bottomsheet-radius-l)
      0 0;
    inset: auto auto 0 50%;
    width: 100%;
    max-width: 420px;
    max-height: calc(100% - 48px);
    transform: translateX(-50%);

    .pl-modal_fade-enter-active &,
    .pl-modal_fade-leave-active & {
      transform: translateX(-50%);
    }

    .pl-modal_fade-enter-from &,
    .pl-modal_fade-leave-to & {
      transform: translate(-50%, 100%);
    }
  }

  &_type-modal {
    min-width: 224px;
    max-width: 420px;
    max-height: min(620px, calc(100% - 64px));
    border-radius: 24px;
    inset: 50% auto auto 50%;
    transform: translate(-50%, -50%);

    .pl-modal_fade-enter-active &,
    .pl-modal_fade-leave-active & {
      transform: translate(-50%, -50%);
    }

    .pl-modal_fade-enter-from &,
    .pl-modal_fade-leave-to & {
      transform: translate(-50%, 100%);
    }
  }

  &_type-fullscreen {
    inset: 0;
    transform: translateY(0);

    .pl-modal_fade-enter-active &,
    .pl-modal_fade-leave-active & {
      transform: translateY(0);
    }

    .pl-modal_fade-enter-from &,
    .pl-modal_fade-leave-to & {
      transform: translateY(100%);
    }
  }
}

.pl-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 10px;

  &__wrapper {
    min-height: 24px;
    flex-shrink: 0;
    width: 100%;
  }

  &__control-wrapper {
    min-width: 54px;
    padding: 0 4px;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    flex-shrink: 0;

    &:last-child {
      justify-content: end;
    }

    .pl-modal_type-fullscreen &:first-child {
      display: none;
    }
  }

  &__control {
    height: 44px;
    display: flex;
    align-items: center;
    cursor: pointer;
  }

  &__title {
    flex-grow: 1;
    text-align: center;
    min-width: 0;
    padding: 10px 0;

    .pl-modal_type-fullscreen & {
      text-align: left;
    }
  }

  &__subtitle {
    padding: 0 20px;
    margin-bottom: 8px;
    text-align: center;
  }

  .pl-modal-header__title-text {
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  }
}

.pl-modal-top {
  width: 100%;
  position: relative;

  &__control {
    position: absolute;
    top: var(--pl-unit-x3);
    right: var(--pl-unit-x3);
    width: var(--pl-unit-x8);
    height: var(--pl-unit-x8);
    display: none;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    border-radius: 100%;
    background: var(--pl-icon-invert-tertiary);
  }
}

.pl-modal-content {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  min-height: var(--pl-unit-x8);
  width: 100%;
  height: 100%;
  overflow: hidden;

  & &-wrapper {
    flex-grow: 1;
    width: 100%;
    height: 100%;
    padding: 0 20px 20px;
    overflow: clip auto;
    overscroll-behavior: contain;
  }

  .pl-modal-header__wrapper ~ & {
    padding-top: 0;
  }

  .pl-modal-footer ~ & {
    padding-bottom: 0;
  }
}

.pl-modal-content-wrapper {
  .pl-modal-wrapper_no-padding & {
    padding: 0;
  }
}

.pl-modal-footer {
  padding: 16px 20px 20px;
  width: 100%;
  order: 1;

  &:empty {
    padding-top: 0;
  }
}

.pl-modal-scrim {
  position: fixed;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  background-color: var(--pl-text-primary);
  cursor: pointer;
  opacity: 0.6;
  z-index: var(--pl-z-index-negative);
  -webkit-tap-highlight-color: transparent;
}

@each $breakpoint in s, m, ml, l, xl {
  @media (--pl-viewport-from-$(breakpoint)) {
    .pl-modal {
      &_type-modal-$(breakpoint),
      &_type-fullscreen-$(breakpoint) {
        .pl-modal-top__control {
          display: flex;
        }
      }
    }
  }
}

@media (--pl-viewport-from-s) {
  .pl-modal {
    &_type-bottom-sheet-s {
      max-height: calc(100% - 48px);
      border-radius: var(--pl-bottomsheet-radius-l)
        var(--pl-bottomsheet-radius-l) 0 0;
      inset: auto auto 0 50%;
      width: 100%;
      max-width: 420px;
      transform: translateX(-50%);

      .pl-modal_fade-enter-active &,
      .pl-modal_fade-leave-active & {
        transform: translateX(-50%);
      }

      .pl-modal_fade-enter-from &,
      .pl-modal_fade-leave-to & {
        transform: translate(-50%, 100%);
      }
    }

    &_type-modal-s {
      width: auto;
      min-width: 250px;
      max-width: 420px;
      max-height: min(620px, calc(100% - 64px));
      border-radius: 24px;
      inset: 50% auto auto 50%;
      transform: translate(-50%, -50%);

      .pl-modal_fade-enter-active &,
      .pl-modal_fade-leave-active & {
        transform: translate(-50%, -50%);
      }

      .pl-modal_fade-enter-from &,
      .pl-modal_fade-leave-to & {
        transform: translate(-50%, 100%);
      }
    }

    &_type-fullscreen-s {
      padding: 0;
      border-radius: 0;
      inset: 0;
      max-width: none;
      max-height: none;
      transform: translateY(0);

      .pl-modal_fade-enter-active &,
      .pl-modal_fade-leave-active & {
        transform: translateY(0);
      }

      .pl-modal_fade-enter-from &,
      .pl-modal_fade-leave-to & {
        transform: translateY(100%);
      }
    }
  }

  .pl-modal-header__control-wrapper:first-child {
    .pl-modal_type-bottom-sheet-s &,
    .pl-modal_type-modal-s & {
      display: flex;
    }

    .pl-modal_type-fullscreen-s & {
      display: none;
    }
  }

  .pl-modal-header__title {
    .pl-modal_type-bottom-sheet-s &,
    .pl-modal_type-modal-s & {
      text-align: center;
    }

    .pl-modal_type-fullscreen-s & {
      text-align: left;
    }
  }
}

@media (--pl-viewport-from-m) {
  .pl-modal {
    .pl-modal-swipe {
      display: none;
    }

    &_type-bottom-sheet-m {
      border-radius: var(--pl-bottomsheet-radius-l)
        var(--pl-bottomsheet-radius-l) 0 0;
      inset: auto auto 0 50%;
      width: 100%;
      max-width: 420px;
      max-height: calc(100% - 48px);
      transform: translateX(-50%);

      .pl-modal_fade-enter-active &,
      .pl-modal_fade-leave-active & {
        transform: translateX(-50%);
      }

      .pl-modal_fade-enter-from &,
      .pl-modal_fade-leave-to & {
        transform: translate(-50%, 100%);
      }
    }

    &_type-modal-m {
      height: auto;
      min-width: 250px;
      max-width: 420px;
      max-height: min(620px, calc(100% - 64px));
      border-radius: 24px;
      inset: 50% auto auto 50%;
      transform: translate(-50%, -50%);

      .pl-modal_fade-enter-active &,
      .pl-modal_fade-leave-active & {
        transform: translate(-50%, -50%);
      }

      .pl-modal_fade-enter-from &,
      .pl-modal_fade-leave-to & {
        transform: translate(-50%, 100%);
      }
    }

    &_type-fullscreen-m {
      padding: 0;
      border-radius: 0;
      inset: 0;
      max-width: none;
      max-height: none;
      transform: translateY(0);

      .pl-modal_fade-enter-active &,
      .pl-modal_fade-leave-active & {
        transform: translateY(0);
      }

      .pl-modal_fade-enter-from &,
      .pl-modal_fade-leave-to & {
        transform: translateY(100%);
      }
    }
  }

  .pl-modal-header__control-wrapper:first-child {
    .pl-modal_type-bottom-sheet-m &,
    .pl-modal_type-modal-m & {
      display: flex;
    }

    .pl-modal_type-fullscreen-m & {
      display: none;
    }
  }

  .pl-modal-header__title {
    .pl-modal_type-bottom-sheet-m &,
    .pl-modal_type-modal-m & {
      text-align: center;
    }

    .pl-modal_type-fullscreen-m & {
      text-align: left;
    }
  }
}

@media (--pl-viewport-from-ml) {
  .pl-modal {
    &_type-bottom-sheet-ml {
      border-radius: var(--pl-bottomsheet-radius-l)
        var(--pl-bottomsheet-radius-l) 0 0;
      inset: auto auto 0 50%;
      width: 100%;
      max-width: 420px;
      max-height: calc(100% - 48px);
      transform: translateX(-50%);

      .pl-modal_fade-enter-active &,
      .pl-modal_fade-leave-active & {
        transform: translateX(-50%);
      }

      .pl-modal_fade-enter-from &,
      .pl-modal_fade-leave-to & {
        transform: translate(-50%, 100%);
      }
    }

    &_type-modal-ml {
      width: auto;
      min-width: 250px;
      max-width: 420px;
      max-height: min(620px, calc(100% - 64px));
      border-radius: 24px;
      inset: 50% auto auto 50%;
      transform: translate(-50%, -50%);

      .pl-modal_fade-enter-active &,
      .pl-modal_fade-leave-active & {
        transform: translate(-50%, -50%);
      }

      .pl-modal_fade-enter-from &,
      .pl-modal_fade-leave-to & {
        transform: translate(-50%, 100%);
      }
    }

    &_type-fullscreen-ml {
      padding: 0;
      border-radius: 0;
      inset: 0;
      max-width: none;
      max-height: none;
      transform: translateY(0);

      .pl-modal_fade-enter-active &,
      .pl-modal_fade-leave-active & {
        transform: translateY(0);
      }

      .pl-modal_fade-enter-from &,
      .pl-modal_fade-leave-to & {
        transform: translateY(100%);
      }
    }
  }

  .pl-modal-header__control-wrapper:first-child {
    .pl-modal_type-bottom-sheet-ml &,
    .pl-modal_type-modal-ml & {
      display: flex;
    }

    .pl-modal_type-fullscreen-ml & {
      display: none;
    }
  }

  .pl-modal-header__title {
    .pl-modal_type-bottom-sheet-ml &,
    .pl-modal_type-modal-ml & {
      text-align: center;
    }

    .pl-modal_type-fullscreen-ml & {
      text-align: left;
    }
  }
}

@media (--pl-viewport-from-l) {
  .pl-modal {
    align-items: center;

    &_type-bottom-sheet-l {
      border-radius: var(--pl-bottomsheet-radius-l)
        var(--pl-bottomsheet-radius-l) 0 0;
      inset: auto auto 0 50%;
      width: 100%;
      max-width: 420px;
      max-height: calc(100% - 48px);
      transform: translateX(-50%);

      .pl-modal_fade-enter-active &,
      .pl-modal_fade-leave-active & {
        transform: translateX(-50%);
      }

      .pl-modal_fade-enter-from &,
      .pl-modal_fade-leave-to & {
        transform: translate(-50%, 100%);
      }
    }

    &_type-modal-l {
      width: auto;
      min-width: 420px;
      max-width: 560px;
      max-height: min(620px, calc(100% - 64px));
      border-radius: 24px;
      inset: 50% auto auto 50%;
      transform: translate(-50%, -50%);

      .pl-modal_fade-enter-active &,
      .pl-modal_fade-leave-active & {
        transform: translate(-50%, -50%);
      }

      .pl-modal_fade-enter-from &,
      .pl-modal_fade-leave-to & {
        transform: translate(-50%, 100%);
      }
    }

    &_type-fullscreen-l {
      padding: 0;
      border-radius: 0;
      inset: 0;
      max-width: none;
      max-height: none;
      transform: translateY(0);

      .pl-modal_fade-enter-active &,
      .pl-modal_fade-leave-active & {
        transform: translateY(0);
      }

      .pl-modal_fade-enter-from &,
      .pl-modal_fade-leave-to & {
        transform: translateY(100%);
      }
    }
  }

  .pl-modal-header__control-wrapper:first-child {
    .pl-modal_type-bottom-sheet-l &,
    .pl-modal_type-modal-l & {
      display: flex;
    }

    .pl-modal_type-fullscreen-l & {
      display: none;
    }
  }

  .pl-modal-header__title {
    .pl-modal_type-bottom-sheet-l &,
    .pl-modal_type-modal-l & {
      text-align: center;
    }

    .pl-modal_type-fullscreen-l & {
      text-align: left;
    }
  }
}

@media (--pl-viewport-from-xl) {
  .pl-modal {
    &_type-bottom-sheet-xl {
      border-radius: var(--pl-bottomsheet-radius-l)
        var(--pl-bottomsheet-radius-l) 0 0;
      inset: auto auto 0 50%;
      width: 100%;
      max-width: 420px;
      max-height: calc(100% - 48px);
      transform: translateX(-50%);

      .pl-modal_fade-enter-active &,
      .pl-modal_fade-leave-active & {
        transform: translateX(-50%);
      }

      .pl-modal_fade-enter-from &,
      .pl-modal_fade-leave-to & {
        transform: translate(-50%, 100%);
      }
    }

    &_type-modal-xl {
      width: auto;
      min-width: 420px;
      max-width: 560px;
      max-height: min(620px, calc(100% - 64px));
      border-radius: 24px;
      inset: 50% auto auto 50%;
      transform: translate(-50%, -50%);

      .pl-modal_fade-enter-active &,
      .pl-modal_fade-leave-active & {
        transform: translate(-50%, -50%);
      }

      .pl-modal_fade-enter-from &,
      .pl-modal_fade-leave-to & {
        transform: translate(-50%, 100%);
      }
    }

    &_type-fullscreen-xl {
      padding: 0;
      border-radius: 0;
      inset: 0;
      max-width: none;
      max-height: none;
      transform: translateY(0);

      .pl-modal_fade-enter-active &,
      .pl-modal_fade-leave-active & {
        transform: translateY(0);
      }

      .pl-modal_fade-enter-from &,
      .pl-modal_fade-leave-to & {
        transform: translateY(100%);
      }
    }
  }

  .pl-modal-header__control-wrapper:first-child {
    .pl-modal_type-bottom-sheet-xl &,
    .pl-modal_type-modal-xl & {
      display: flex;
    }

    .pl-modal_type-fullscreen-xl & {
      display: none;
    }
  }

  .pl-modal-header__title {
    .pl-modal_type-bottom-sheet-xl &,
    .pl-modal_type-modal-xl & {
      text-align: center;
    }

    .pl-modal_type-fullscreen-xl & {
      text-align: left;
    }
  }
}
</style>
